import { StateCreator } from 'zustand';
import {
  HighwaysDamageType,
  PAVEMENT_DAMAGE_TYPES,
  ROAD_DAMAGE_TYPES,
} from '@highways/constants/damageTypes';
import { PartitionFileObjectsType } from '@highways/utils/convertFileObjects';
import { ServiceLocationDetailsObjectType } from '@highways/api/type';
import { AssetType, FeatureAttributes } from '@types';

export enum HighwaysType {
  Road = 'road',
  Pavement = 'pavement',
}

export interface DamageTypes {
  title: HighwaysDamageType;
  description: string;
  image: string;
}

export const emptyServiceAssetDetails: FeatureAttributes = {
  site_class_code: '',
  site_class_name: '',
  central_asset_id: '',
  contract_area_code: '',
  feature_type_code: '',
  layer: '' as AssetType,
};

// fill in mock data for testing -> to remove in production
export const emptyServiceLocationDetails = {
  address_line_1: '',
  address_line_2: '',
  country: '',
  county: '',
  easting: null,
  latitude: null,
  longitude: null,
  northing: null,
  postcode: '',
  town_city: '',
  UPRN: '',
  USRN: '',
};

export type BinaryOptionsType = 'Yes' | 'No' | '';

export interface HighwaysDetailsSlice {
  highwaysType: HighwaysType | null;
  damageTypeOptions: DamageTypes[] | null;
  damageType: HighwaysDamageType | null;
  anythingElseText: string;
  uploadedImages: string[];
  uploadedImagesFileName: string[];
  uploadedImagesFileType: string[];
  uploadedImagesBlob: {
    blob_uri: string;
    blob_endpoint: string;
    filename: string;
  }[];
  fileDetailsObjects: { name: string; path: string }[];
  wantConfirmationEmail: BinaryOptionsType;
  serviceAssetDetails: FeatureAttributes;
  serviceLocationDetails: ServiceLocationDetailsObjectType;
  updateWantConfirmationEmail: (
    wantConfirmationEmail: BinaryOptionsType,
  ) => void;
  updateAnythingElseText: (anythingElseText: string) => void;
  updateDamageType: (damageType: HighwaysDamageType) => void;
  hasDamageCausedInjury: BinaryOptionsType;
  updateHasDamageCausedInjury: (
    hasDamageCausedInjuryPage: BinaryOptionsType,
  ) => void;
  updateServiceAssetDetails: (serviceAssetDetails: FeatureAttributes) => void;
  updateServiceLocationDetails: (
    serviceLocationDetails: ServiceLocationDetailsObjectType,
  ) => void;
  damageTypes: DamageTypes[] | null;
  updateHighwaysType: (highwaysType: HighwaysType) => void;
  caseReferenceId: string;
  slaText: string;
  updateCaseReferenceId: (caseReferenceId: string) => void;
  updateSlaText: (slaText: string | undefined) => void;
  updateUploadedImages: (uploadedImages: PartitionFileObjectsType) => void;
  updateUploadedImagesBlobs: (
    uploadedImagesBlob: {
      blob_uri: string;
      blob_endpoint: string;
      filename: string;
    }[],
  ) => void;
  resetHighwaysDetails: () => void;
  isPhotoLocationStored: boolean;
  updateIsPhotoLocationStored: (isPhotoLocationStored: boolean) => void;
  fileWithLoadedCoordinates: string;
  updateFileWithLoadedCoordinates: (fileWithLoadedCoordinates: string) => void;
}

export const createHighwaysDetailsSlice: StateCreator<
  HighwaysDetailsSlice,
  [],
  [],
  HighwaysDetailsSlice
> = (set, get) => ({
  highwaysType: null,
  damageTypeOptions: null,
  damageType: null,
  updateDamageType: (damageType) => set(() => ({ damageType })),
  uploadedImages: [],
  uploadedImagesFileName: [],
  uploadedImagesFileType: [],
  uploadedImagesBlob: [],
  fileDetailsObjects: [],
  damageTypes: null,
  wantConfirmationEmail: '',
  slaText: '',
  fileWithLoadedCoordinates: '',
  updateFileWithLoadedCoordinates: (fileWithLoadedCoordinates: string) =>
    set(() => ({ fileWithLoadedCoordinates })),
  serviceAssetDetails: emptyServiceAssetDetails,
  serviceLocationDetails: emptyServiceLocationDetails,
  updateSlaText: (slaText) => set(() => ({ slaText })),
  updateServiceAssetDetails: (serviceAssetDetails) =>
    set(() => ({ serviceAssetDetails })),
  updateServiceLocationDetails: (serviceLocationDetails) =>
    set(() => ({ serviceLocationDetails })),
  updateWantConfirmationEmail: (wantConfirmationEmail) =>
    set(() => ({ wantConfirmationEmail })),
  updateUploadedImagesBlobs: (uploadedImagesBlob) =>
    set(() => ({ uploadedImagesBlob })),
  // File type is not serializable in zustand, so we need to convert the File objects to data URLs
  updateUploadedImages: (uploadedFileParts: PartitionFileObjectsType) => {
    const {
      uploadedImages,
      uploadedImagesFileName,
      uploadedImagesFileType,
      uploadedImagesBlob,
      geoDataArrays,
    } = uploadedFileParts;
    const fileDetailsObjects = uploadedImagesFileName.map(
      (filename, index) => ({
        name: filename,
        path: uploadedImagesBlob[index].blob_uri,
      }),
    );
    const { serviceLocationDetails, updateFileWithLoadedCoordinates } = get();
    // update the service location details with the first geo data found
    if (
      !serviceLocationDetails.latitude &&
      !serviceLocationDetails.longitude &&
      geoDataArrays.length > 0
    ) {
      geoDataArrays.some((geoData) => {
        if (
          geoData.latitude !== null &&
          geoData.longitude !== null &&
          geoData.fileName !== null
        ) {
          updateFileWithLoadedCoordinates(geoData.fileName);
          set((state) => ({
            serviceLocationDetails: {
              ...state.serviceLocationDetails,
              latitude: geoData.latitude,
              longitude: geoData.longitude,
            },
          }));
          // break the loop if the first geo data is found
          return true;
        }
        // continue the loop
        return false;
      });
    }
    // update the service location details with the first geo data found
    set({ uploadedImages });
    set({ uploadedImagesFileName });
    set({ uploadedImagesFileType });
    set({ uploadedImagesBlob });
    set({ fileDetailsObjects });
  },
  isPhotoLocationStored: false,
  updateIsPhotoLocationStored: (isPhotoLocationStored) =>
    set(() => ({ isPhotoLocationStored })),
  anythingElseText: '',
  updateAnythingElseText: (anythingElseText) =>
    set(() => ({ anythingElseText })),
  hasDamageCausedInjury: '',
  updateHasDamageCausedInjury: (hasDamageCausedInjury) =>
    set(() => ({ hasDamageCausedInjury })),
  updateHighwaysType: (highwaysType) => {
    set(() => ({ highwaysType }));

    switch (highwaysType) {
      case HighwaysType.Pavement:
        set(() => {
          return {
            damageTypeOptions: PAVEMENT_DAMAGE_TYPES,
          };
        });
        break;
      case HighwaysType.Road:
        set(() => {
          return {
            damageTypeOptions: ROAD_DAMAGE_TYPES,
          };
        });
        break;
      default:
        set(() => {
          return {
            damageTypeOptions: ROAD_DAMAGE_TYPES,
          };
        });
        break;
    }
  },
  caseReferenceId: '',
  updateCaseReferenceId: (caseReferenceId) => set(() => ({ caseReferenceId })),
  resetHighwaysDetails: () =>
    set(() => ({
      damageType: null,
      hasDamageCausedInjury: '',
      anythingElseText: '',
      uploadedImages: [],
      uploadedImagesFileName: [],
      uploadedImagesFileType: [],
      uploadedImagesBlob: [],
      fileDetailsObjects: [],
      serviceAssetDetails: emptyServiceAssetDetails,
      serviceLocationDetails: emptyServiceLocationDetails,
    })),
});
